import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "[&]:text-white !important [&_svg]:text-white !important [&>span]:text-white !important bg-primary hover:bg-primary/90",
        destructive: "[&]:text-white !important [&_svg]:text-white !important [&>span]:text-white !important bg-destructive hover:bg-destructive/90",
        outline: "[&]:text-primary !important [&_svg]:text-primary !important [&>span]:text-primary !important border border-input bg-background hover:bg-accent hover:[&]:text-accent-foreground hover:[&_svg]:text-accent-foreground hover:[&>span]:text-accent-foreground",
        secondary: "[&]:text-white !important [&_svg]:text-white !important [&>span]:text-white !important bg-secondary hover:bg-secondary/80",
        ghost: "[&]:text-primary !important [&_svg]:text-primary !important [&>span]:text-primary !important hover:bg-accent hover:[&]:text-accent-foreground hover:[&_svg]:text-accent-foreground hover:[&>span]:text-accent-foreground",
        "ghost-contrast": "[&]:text-foreground !important [&_svg]:text-foreground !important [&>span]:text-foreground !important bg-background/80 backdrop-blur-sm border border-border/50 shadow-sm hover:bg-background hover:border-border hover:[&]:text-foreground hover:[&_svg]:text-foreground hover:[&>span]:text-foreground",
        link: "[&]:text-primary !important [&_svg]:text-primary !important [&>span]:text-primary !important underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

export type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "ghost-contrast" | "link";
